import React from "react";

import {SelectOption} from "../../../../../elements/select/SelectDropdown";
import {ValidatedMultiSelectField} from "../../../../../elements/validator/component/ValidatedMultiSelectField";
import {
    ERRORS_HOLDER,
    SelectedLocationsValidator,
    ValidatorOutcome,
    ValueValidator
} from "../../../../../elements/validator/Validator";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {deriveLocationsFromFranchises, FranchiseRequestUi} from "../../../franchiseTypes";
import {BotFormSelectionProvider, BotRequest, BotRequestPartial} from "../BotForm";
import {useAppSelector} from "../../../../../../app/hooks";

interface Props<T> {
    getMessage: (s: any) => any;
    updateRequest: (update: BotRequestPartial) => void;
    franchiseOptions: SelectOption<FranchiseRequestUi>[];
    selectionProvider: BotFormSelectionProvider;
    updateValidationStatus: (status: ValidationResult<T>) => void;
}

const normalizeManufacturer = (value?: string): string | undefined => {
    const normalized = (value || "").trim().toUpperCase();
    return normalized || undefined;
}

const validateEnabledNewVehicleManufacturers = (franchiseList: FranchiseRequestUi[]): ValidatorOutcome => {
    const usedManufacturersByVehicleType = new Set<string>();
    for (const franchise of franchiseList) {
        const configuration = franchise.newVehicleDataConfiguration;
        if (!configuration?.isEnabled) {
            continue;
        }

        const manufacturer = normalizeManufacturer(configuration.manufacturer);
        if (!manufacturer) {
            return {
                valid: false,
                message: "Every selected Franchise with New Vehicles enabled must have a manufacturer"
            };
        }

        const key = `${configuration.vehicleType}:${manufacturer}`;
        if (usedManufacturersByVehicleType.has(key)) {
            return {
                valid: false,
                message: `Selected Franchises cannot use the same New Vehicles manufacturer and vehicle type: ${manufacturer}`
            };
        }
        usedManufacturersByVehicleType.add(key);
    }

    return ERRORS_HOLDER.VALID;
}

export const FranchiseInformation = <T extends BotRequest>({
                                                               getMessage,
                                                               updateRequest,
                                                               franchiseOptions,
                                                               selectionProvider,
                                                               updateValidationStatus
                                                           }: Props<T>) => {
    const assistantGroupHash = useAppSelector(selectionProvider.groupHash);

    const selectedFranchisesValidator: ValueValidator<FranchiseRequestUi[]> = (fieldName, value) => {
        if (!value || value.length === 0) {
            return ERRORS_HOLDER.EMPTY_LIST(fieldName);
        }

        const locationValidation = SelectedLocationsValidator(
            "Locations",
            deriveLocationsFromFranchises(value),
            assistantGroupHash
        ) as ValidatorOutcome;
        if (!locationValidation.valid) {
            return locationValidation;
        }

        return validateEnabledNewVehicleManufacturers(value);
    };

    return <ValidatedMultiSelectField<FranchiseRequestUi, T>
        onChange={(franchiseList) => updateRequest({franchiseList})}
        updateValidationStatus={updateValidationStatus}
        fieldName={"franchiseList"}
        selector={selectionProvider.franchiseList}
        validator={selectedFranchisesValidator}
        label={"Franchise(s)"}
        getMessage={getMessage}
        options={franchiseOptions}
        isEqual={(a, b) => a?.publicId === b?.publicId}
    />
}
